import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";

import { productChangeOccurrenceKey, type ProductChangeAlert } from "../calcs/lib/features/product-alerts";
import {
  dropHandledPriceAlertFirings,
  normalizePriceAlertEntry,
  rememberHandledPriceAlertFirings,
  type PriceAlertSidebarEntry,
} from "./priceAlerts";

export type SidebarState = {
  isOpen: boolean;
  products: ProductChangeAlert[];
  priceAlerts: PriceAlertSidebarEntry[];
  /** Firings the user has read or repeated, keyed per firing. */
  handledPriceAlertFirings: string[];
  initialWhatsNewExpanded: boolean;
  initialCurveChangesExpanded: boolean;
  initialPermissionChangesExpanded: boolean;
  initialPriceAlertsExpanded: boolean;
};

export type SidebarMode = "full-height" | "grid";

const initialState: SidebarState = {
  isOpen: false,
  products: [],
  priceAlerts: [],
  handledPriceAlertFirings: [],
  initialWhatsNewExpanded: false,
  initialCurveChangesExpanded: false,
  initialPermissionChangesExpanded: false,
  initialPriceAlertsExpanded: false,
};

export const sidebarStateAtom = atomWithStorage<SidebarState>("alerts-sidebar-state", initialState);
export const sidebarModeAtom = atomWithStorage<SidebarMode>("sidebar-mode", "grid");

/** Occurrence keys of curve/permission cards that were on screen while their accordion was open. */
const seenProductAlertKeys = new Set<string>();

export const rememberSeenProductAlertKeys = (keys: Iterable<string>) => {
  for (const key of keys) {
    if (key) seenProductAlertKeys.add(key);
  }
};

/**
 * Drops cards the user has already seen. Pass `shouldDrop` when collapsing one accordion so
 * visible cards in the other section are not marked read. With no predicate (panel close),
 * every seen card is dropped and the seen set is cleared.
 */
export const dropSeenProductAlerts = (
  products: ProductChangeAlert[],
  shouldDrop?: (alert: ProductChangeAlert) => boolean,
): ProductChangeAlert[] => {
  if (seenProductAlertKeys.size === 0) return products;
  const remaining = products.filter((item) => {
    if (!seenProductAlertKeys.has(productChangeOccurrenceKey(item))) return true;
    return shouldDrop != null && !shouldDrop(item);
  });
  if (shouldDrop == null) {
    seenProductAlertKeys.clear();
    return remaining;
  }
  for (const item of products) {
    if (shouldDrop(item)) seenProductAlertKeys.delete(productChangeOccurrenceKey(item));
  }
  return remaining;
};

export const resetSeenProductAlerts = () => {
  seenProductAlertKeys.clear();
};

/**
 * Closes the panel. Unread curve/permission cards stay until they were visible in an open accordion.
 * Unread (never expanded) price alerts stay so they survive open/close; if that accordion was left
 * open, those cards were viewed and are marked read.
 */
export const closeNotificationsPanel = (prev: SidebarState): SidebarState => {
  const viewedPrice = prev.initialPriceAlertsExpanded;
  return {
    ...prev,
    isOpen: false,
    products: dropSeenProductAlerts(prev.products),
    priceAlerts: viewedPrice ? [] : prev.priceAlerts,
    handledPriceAlertFirings: viewedPrice
      ? rememberHandledPriceAlertFirings(prev.handledPriceAlertFirings ?? [], prev.priceAlerts)
      : (prev.handledPriceAlertFirings ?? []),
    initialWhatsNewExpanded: false,
    initialCurveChangesExpanded: false,
    initialPermissionChangesExpanded: false,
    initialPriceAlertsExpanded: false,
  };
};

export const closedNotificationCentre = closeNotificationsPanel;
export const useSidebarState = () => {
  const [state, setState] = useAtom(sidebarStateAtom);
  const handledPriceAlertFirings = state.handledPriceAlertFirings ?? [];
  return [
    {
      ...initialState,
      ...state,
      handledPriceAlertFirings,
      // Filtered here rather than at each caller, so no surface can show a firing the user has
      // already read or repeated, however it came to be on the stored list.
      priceAlerts: dropHandledPriceAlertFirings(
        (state.priceAlerts ?? []).map((entry) => normalizePriceAlertEntry(entry)),
        handledPriceAlertFirings,
      ),
    },
    setState,
  ] as const;
};
export const useSidebarMode = () => useAtom(sidebarModeAtom);
